import { Avatar, Tooltip } from "antd";
import { ComponentProps, CSSProperties, ReactElement } from "react";
import {
  FilterMenu,
  FilterMenuOptions,
} from "src/search/components/inputs/FilterMenu";
import { SearchContext } from "src/search/useSearchContext";
import { valueToLabel } from "pages/[orgId]/explore/lib/common";
import { usePopupContext } from "src/hooks/usePopup";
import { ContentView } from "pages/[orgId]/explore/lib/useContentView";
import { ValueFormatter } from "src/utils/formatters";
import { Field } from "src/search/types";
import { formatValue } from "src/utils/strings";
import { columnInfo } from "@vzbl/integrations";
import { Api } from "@vzbl/integrations";
import { asKey, fromKey } from "src/utils/fields";
import { UserAvatar } from "src/components/UserAvatar";
// A tag-like wrapper around the FilterMenu

export const FilterTag = (
  props: {
    context: SearchContext;
    id?: string;
    icon?: ReactElement;
    style?: CSSProperties;
    options?: FilterMenuOptions;
  } & Pick<
    ComponentProps<typeof FilterMenu>,
    "values" | "properties" | "entities"
  >
) => {
  const popup = usePopupContext();
  const property = props.properties?.at(0);
  if (!property) throw new Error("expected at least 1 property");
  return (
    <div style={{ height: 24 }}>
      <div
        id={props.id}
        style={{
          display: "inline-block",
        }}
        onClick={(e) => {
          e.stopPropagation();
          popup.setPopup(e, <FilterMenu {...props} options={props.options} />);
        }}
      >
        <FilterTagButton {...props} />
      </div>
    </div>
  );
};

const FilterTagButton = (
  props: {
    context: SearchContext;
    id?: string;
    icon?: ReactElement;
    maxWidth?: string | number;
    disabled?: boolean;
    style?: CSSProperties;
  } & Pick<ComponentProps<typeof FilterMenu>, "values">
) => {
  return (
    <div
      id={props.id}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 4,
        maxWidth: props.maxWidth || "100%",
        overflow: "hidden",
        fontSize: 12,
        height: 24,
      }}
      className={`${props.disabled ? "disabled" : ""} filter-tag-button`}
    >
      {props.icon && <div>{props.icon}</div>}
      <div
        style={{
          minWidth: 0,
          flex: 1,
          maxWidth: "100%",
        }}
      >
        <div className="trunc">
          {props.values[0].label === "__VZBL::null__" ? (
            <span style={{ opacity: 0.5 }}>null</span>
          ) : (
            props.values[0].label
          )}
        </div>
      </div>
    </div>
  );
};

export function getFilterTagEntitiesProp(
  uuid: string,
  record: any,
  context: SearchContext
) {
  const cellEntity = uuid && uuid !== "__VZBL::null__" ? { uuid } : undefined;
  const entityTypeKey = Object.keys(record).find((key) =>
    key.endsWith("EntityType")
  );

  const rowEntity = record.entityUuid
    ? {
        uuid: record.entityUuid || (record.uuid as string),
        label: (record[entityTypeKey || ""] ||
          context.params.state.select?.at(0) ||
          "Event") as string,
      }
    : undefined;
  return [cellEntity, rowEntity].filter(Boolean) as Exclude<
    typeof cellEntity | typeof rowEntity,
    undefined
  >[];
}

/**
 * get the props for the value so we can display filter optoins like show Person, show Commit, etc.. This identifies the entiteis in the payload
 * @param value - either entity object or a cell value
 * @param record - data record that contains aggregations, entities, metadata, etc.
 * @param field - definition of the data we are displaying (e.g. {target: "person", attribute: "name"})
 * @param context - search context
 */
export function propsForValue(
  value: any,
  record: any,
  field: Field,
  context: SearchContext
) {
  const colInfo = (Array.isArray(columnInfo) ? columnInfo : []).find(
    (c: { key: string }) => c.key == asKey(field)
  ) || {
    type: "string",
    unit: undefined,
    ...field,
  };

  if (value && value["uuid"]) {
    const label = value["value"];
    if (label == "__VZBL::null__") {
      return {
        properties: [field],
        values: [{ label, value: value.value }],
        entities: getFilterTagEntitiesProp("", record, context),
      };
    }
    return {
      properties: [field],
      values: [{ label: `${label || ""}`, value: value.value }],
      entities: getFilterTagEntitiesProp(value.uuid, record, context),
      icon: value["avatarUrl"] ? (
        <UserAvatar
          size="small"
          showName={false}
          user={{
            avatarUrl: value["avatarUrl"],
            name: label,
            uuid: value.uuid,
          }}
          showTooltip={false}
        />
      ) : undefined,
    };
  } else {
    return {
      properties: [field],
      values: [
        { label: formatValue(value, colInfo.type, colInfo.unit), value },
      ],
      entities: getFilterTagEntitiesProp("", record, context),
    };
  }
}
